import {
  ProductCardButtonStyle,
  ProductCardDisplayConfig,
  ProductCardFontSize,
  ProductCardImageFit,
  ProductCardImageRatio,
  ProductCardMaxLines
} from './product-card-display.model';
import {
  PRODUCT_CARD_LAYOUT_TYPES,
  ProductCardLayoutLegacyRoot,
  ProductCardLayoutSavedConfig,
  ProductCardLayoutsRoot,
} from './product-card-layout.model';
import { ProductCardLayoutType } from './product-card-display.model';
import { MlTextRecord } from '../../service/ml-description.service';
import {
  formatProductCardPriceAmount,
  getProductCardCurrencySymbol,
  parseProductCardCurrencyDisplayStyle,
  resolveProductCardCurrencyUnit,
  resolveProductCardProductCurrencyCode
} from './product-card-currency-format';

const LEGACY_LAYOUT_KEYS = new Set([
  'activeCardType',
  'cardType',
  'layouts',
  'cornerRadius',
  'elementOrder',
  'wishlistEnabled',
  'wishlistVisible',
  'imageRatio',
  'imageFit',
  'imageVisible',
  'titleSource',
  'titleFontSize',
  'titleFontWeight',
  'titleMaxLines',
  'titleVisible',
  'skuSource',
  'skuFontSize',
  'skuFontWeight',
  'skuMaxLines',
  'skuVisible',
  'skuTitleMl',
  'skuTitle',
  'descriptionSource',
  'descriptionFontSize',
  'descriptionFontWeight',
  'descriptionMaxLines',
  'descriptionVisible',
  'availableLabel',
  'preOrderLabel',
  'limitedLabel',
  'availabilityVisible',
  'priceTitleMl',
  'priceTitle',
  'priceCurrencyFormat',
  'symbolActivationEnabled',
  'symbolStyle',
  'currencySymbolPosition',
  'priceVisible',
  'countryAssignment',
  'addToCartButtonStyle',
  'addToCartVisible',
  'compareButtonStyle',
  'compareButtonVisible',
]);

function isLegacyFlatProductCardRoot(raw: Record<string, unknown>): boolean {
  if (raw['layouts'] && typeof raw['layouts'] === 'object') {
    return false;
  }
  for (const key of LEGACY_LAYOUT_KEYS) {
    if (key in raw) {
      return true;
    }
  }
  return false;
}

function extractLegacyProductCardConfig(raw: ProductCardLayoutLegacyRoot): ProductCardLayoutSavedConfig {
  const { cardType: _cardType, ...config } = raw;
  return config;
}

/** Normalizes stored payload to the per-variant layouts root shape. */
export function normalizeProductCardLayoutsRoot(raw: unknown): ProductCardLayoutsRoot {
  if (!raw || typeof raw !== 'object') {
    return { activeCardType: 'vertical', layouts: {} };
  }

  const record = raw as Record<string, unknown>;

  if (record['layouts'] && typeof record['layouts'] === 'object') {
    return {
      activeCardType:
        (record['activeCardType'] as ProductCardLayoutType | undefined) ??
        (record['cardType'] as ProductCardLayoutType | undefined) ??
        'vertical',
      layouts: record['layouts'] as Partial<Record<ProductCardLayoutType, ProductCardLayoutSavedConfig>>,
    };
  }

  if (isLegacyFlatProductCardRoot(record)) {
    const legacy = record as ProductCardLayoutLegacyRoot;
    const activeCardType = legacy.cardType ?? 'vertical';
    const config = extractLegacyProductCardConfig(legacy);
    return {
      activeCardType,
      layouts: {
        vertical: config,
        horizontal: config,
        line: config,
      },
    };
  }

  return { activeCardType: 'vertical', layouts: {} };
}

export function hasProductCardMlTextContent(value: unknown): boolean {
  if (value == null) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return Object.values(value as Record<string, unknown>).some(
      (entry) => typeof entry === 'string' && entry.trim().length > 0
    );
  }
  return false;
}

function pickProductCardMlLabel(
  primary: MlTextRecord | undefined,
  fallback: MlTextRecord | undefined
): MlTextRecord | undefined {
  if (hasProductCardMlTextContent(primary)) {
    return primary;
  }
  if (hasProductCardMlTextContent(fallback)) {
    return fallback;
  }
  return primary;
}

/** Layout that defines custom availability labels (used as fallback for other variants). */
export function findProductCardAvailabilityLabelsDonor(
  layouts: Partial<Record<ProductCardLayoutType, ProductCardLayoutSavedConfig>> | undefined
): ProductCardLayoutSavedConfig | null {
  if (!layouts) {
    return null;
  }

  for (const cardType of PRODUCT_CARD_LAYOUT_TYPES) {
    const layout = layouts[cardType];
    if (!layout) {
      continue;
    }
    if (
      hasProductCardMlTextContent(layout.availableLabel) ||
      hasProductCardMlTextContent(layout.preOrderLabel) ||
      hasProductCardMlTextContent(layout.limitedLabel)
    ) {
      return layout;
    }
  }

  return null;
}

function mergeAvailabilityLabelsFromDonor(
  saved: ProductCardLayoutSavedConfig,
  donor: ProductCardLayoutSavedConfig
): ProductCardLayoutSavedConfig {
  return {
    ...saved,
    availableLabel: pickProductCardMlLabel(saved.availableLabel, donor.availableLabel),
    preOrderLabel: pickProductCardMlLabel(saved.preOrderLabel, donor.preOrderLabel),
    limitedLabel: pickProductCardMlLabel(saved.limitedLabel, donor.limitedLabel),
  };
}

export function resolveProductCardLayoutForType(
  root: ProductCardLayoutsRoot | null | undefined,
  cardType: ProductCardLayoutType
): ProductCardLayoutSavedConfig | null {
  const saved = root?.layouts?.[cardType];
  if (!saved || typeof saved !== 'object') {
    return null;
  }

  const donor = findProductCardAvailabilityLabelsDonor(root?.layouts);
  if (!donor || donor === saved) {
    return saved;
  }

  return mergeAvailabilityLabelsFromDonor(saved, donor);
}

export function mapProductCardLayoutsRootToDisplayConfigs(
  root: ProductCardLayoutsRoot | null | undefined
): Partial<Record<ProductCardLayoutType, ProductCardDisplayConfig | null>> {
  const configs: Partial<Record<ProductCardLayoutType, ProductCardDisplayConfig | null>> = {};
  for (const cardType of PRODUCT_CARD_LAYOUT_TYPES) {
    configs[cardType] = mapProductCardLayoutToDisplayConfig(resolveProductCardLayoutForType(root, cardType));
  }
  return configs;
}

/** Maps persisted flexible-layout payload to runtime product card display overrides. */
export function mapProductCardLayoutToDisplayConfig(
  saved: ProductCardLayoutSavedConfig | null | undefined
): ProductCardDisplayConfig | null {
  if (!saved || typeof saved !== 'object') {
    return null;
  }

  const priceTitle = saved.priceTitleMl ?? saved.priceTitle;
  const skuTitle = saved.skuTitleMl ?? saved.skuTitle;

  return {
    elementOrder: saved.elementOrder?.length ? [...saved.elementOrder] : undefined,
    cornerRadius: saved.cornerRadius,
    wishlistVisible: saved.wishlistVisible,
    wishlistEnabled: saved.wishlistEnabled,
    imageVisible: saved.imageVisible,
    imageRatio: saved.imageRatio,
    imageFit: saved.imageFit,
    titleVisible: saved.titleVisible,
    titleSource: saved.titleSource || undefined,
    titleFontSize: saved.titleFontSize,
    titleFontWeight: saved.titleFontWeight,
    titleMaxLines: saved.titleMaxLines,
    skuVisible: saved.skuVisible,
    skuTitle,
    skuSource: saved.skuSource || undefined,
    skuFontSize: saved.skuFontSize,
    skuFontWeight: saved.skuFontWeight,
    skuMaxLines: saved.skuMaxLines,
    descriptionVisible: saved.descriptionVisible,
    descriptionSource: saved.descriptionSource || undefined,
    descriptionFontSize: saved.descriptionFontSize,
    descriptionFontWeight: saved.descriptionFontWeight,
    descriptionMaxLines: saved.descriptionMaxLines,
    availabilityVisible: saved.availabilityVisible,
    availableLabel: saved.availableLabel,
    preOrderLabel: saved.preOrderLabel,
    limitedLabel: saved.limitedLabel,
    priceVisible: saved.priceVisible,
    priceTitle,
    priceCurrencyFormat: saved.priceCurrencyFormat,
    symbolActivationEnabled: saved.symbolActivationEnabled,
    symbolStyle: saved.symbolStyle,
    currencySymbolPosition: saved.currencySymbolPosition,
    addToCartVisible: saved.addToCartVisible,
    addToCartButtonStyle: saved.addToCartButtonStyle,
    compareButtonVisible: saved.compareButtonVisible,
    compareButtonStyle: saved.compareButtonStyle,
  };
}

export function isProductCardElementVisible(
  config: ProductCardDisplayConfig | null | undefined,
  flag: keyof Pick<
    ProductCardDisplayConfig,
    | 'wishlistVisible'
    | 'imageVisible'
    | 'titleVisible'
    | 'skuVisible'
    | 'descriptionVisible'
    | 'availabilityVisible'
    | 'priceVisible'
    | 'addToCartVisible'
    | 'compareButtonVisible'
  >
): boolean {
  if (!config) {
    return true;
  }
  return config[flag] !== false;
}

export function productCardBorderRadius(config: ProductCardDisplayConfig | null | undefined): string | null {
  if (config?.cornerRadius === undefined || config.cornerRadius === null) {
    return null;
  }
  return `${config.cornerRadius}px`;
}

export function productCardImageRatioClass(config: ProductCardDisplayConfig | null | undefined): string {
  switch (config?.imageRatio) {
    case '1:1':
      return 'ratio-1x1';
    case '3:4':
      return 'ratio-3x4';
    case '16:9':
      return 'ratio-16x9';
    case '4:3':
    default:
      return 'ratio-4x3';
  }
}

export function productCardImageFillLayout(config: ProductCardDisplayConfig | null | undefined): boolean {
  return config?.imageFit === 'fill';
}

export function productCardImageObjectFit(config: ProductCardDisplayConfig | null | undefined): 'cover' | 'contain' {
  return config?.imageFit === 'contain' ? 'contain' : 'cover';
}

export function productCardFontSizeRem(size: ProductCardFontSize | undefined): string | null {
  switch (size) {
    case 'sm':
      return '0.875rem';
    case 'lg':
      return '1.125rem';
    case 'md':
      return '1rem';
    default:
      return null;
  }
}

export function productCardLineClampStyle(lines: ProductCardMaxLines | undefined): Record<string, string | number> | null {
  if (!lines) {
    return null;
  }
  return {
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    'text-overflow': 'ellipsis',
    'word-break': 'break-word',
    '-webkit-line-clamp': lines,
    'line-clamp': lines,
  };
}

type ProductCardTextSection = 'title' | 'sku' | 'description';

/** Typography overrides shared by title, SKU, and description rows. */
export function productCardTextSectionStyles(
  config: ProductCardDisplayConfig | null | undefined,
  section: ProductCardTextSection
): Record<string, string | number> {
  const styles: Record<string, string | number> = {};
  const fontSize = productCardFontSizeRem(
    section === 'title'
      ? config?.titleFontSize
      : section === 'sku'
        ? config?.skuFontSize
        : config?.descriptionFontSize
  );
  if (fontSize) {
    styles['font-size'] = fontSize;
  }

  const fontWeight =
    section === 'title'
      ? config?.titleFontWeight
      : section === 'sku'
        ? config?.skuFontWeight
        : config?.descriptionFontWeight;
  if (fontWeight) {
    styles['font-weight'] = fontWeight;
  }

  const maxLines =
    section === 'title'
      ? config?.titleMaxLines
      : section === 'sku'
        ? config?.skuMaxLines
        : config?.descriptionMaxLines;
  const lineClamp = productCardLineClampStyle(maxLines);
  return lineClamp ? { ...styles, ...lineClamp } : styles;
}

export function productCardButtonClass(style: ProductCardButtonStyle | undefined): string {
  switch (style) {
    case 'ghost':
      return 'btn btn--ghost';
    case 'text':
      return 'btn btn--ghost2';
    default:
      return 'btn btn--primary';
  }
}

export function resolveProductCardPriceValue(
  product: { General_Information?: { price?: number | string } } | null | undefined
): number {
  const raw = product?.General_Information?.price;
  if (raw === null || raw === undefined || raw === '') {
    return 0;
  }
  const numeric = typeof raw === 'number' ? raw : Number(String(raw).trim());
  return Number.isFinite(numeric) ? numeric : 0;
}

export type ProductCardPriceFormatConfig = Pick<
  ProductCardDisplayConfig,
  'priceCurrencyFormat' | 'currencySymbolPosition' | 'symbolActivationEnabled' | 'symbolStyle' | 'symbolStyleValues'
>;

export function formatProductCardPriceWithConfig(
  amount: number | string,
  product: { General_Information?: { currency?: string } } | null | undefined,
  config: ProductCardPriceFormatConfig | null | undefined
): string {
  const productCurrency = resolveProductCardProductCurrencyCode(product);
  const displayStyle = config?.priceCurrencyFormat
    ? parseProductCardCurrencyDisplayStyle(config.priceCurrencyFormat)
    : 'symbol';
  const formattedAmount = formatProductCardPriceAmount(amount ?? 0);

  if (!productCurrency) {
    return formattedAmount;
  }

  const parsed = {
    currencyCode: productCurrency,
    displayStyle
  };

  const unit = config?.symbolActivationEnabled
    ? config.symbolStyle?.trim() ||
      config.symbolStyleValues?.find((value) => value.trim()) ||
      getProductCardCurrencySymbol(parsed.currencyCode)
    : resolveProductCardCurrencyUnit(parsed);
  const position = config?.currencySymbolPosition ?? 'before';
  const usesCompactSpacing =
    config?.symbolActivationEnabled || (parsed.displayStyle === 'symbol' && unit.length <= 2);
  const spacer = usesCompactSpacing ? '' : ' ';

  return position === 'after'
    ? `${formattedAmount}${spacer}${unit}`
    : `${unit}${spacer}${formattedAmount}`;
}

export function productCardFormattedPrice(
  product: { General_Information?: { price?: number | string; currency?: string } } | null | undefined,
  config: ProductCardDisplayConfig | null | undefined
): string {
  return formatProductCardPriceWithConfig(resolveProductCardPriceValue(product), product, config);
}

export function resolveProductCardMlLabel(
  value: string | MlTextRecord | undefined | null,
  preferredLang?: string
): string {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }

  const lang =
    preferredLang ??
    (
      (typeof localStorage !== 'undefined' && localStorage.getItem('langchanged')) ||
      (typeof localStorage !== 'undefined' && localStorage.getItem('lang')) ||
      'en'
    );

  let resolved = value[lang];
  if (resolved != null && String(resolved).trim() !== '') {
    return String(resolved).trim();
  }
  resolved = value[lang?.toLowerCase()];
  if (resolved != null && String(resolved).trim() !== '') {
    return String(resolved).trim();
  }
  resolved = value[lang?.toUpperCase()];
  if (resolved != null && String(resolved).trim() !== '') {
    return String(resolved).trim();
  }
  resolved = value['en'] ?? value['En'] ?? value['EN'];
  if (resolved != null && String(resolved).trim() !== '') {
    return String(resolved).trim();
  }

  const keys = Object.keys(value);
  if (keys.length > 0) {
    const first = value[keys[0]];
    return first != null ? String(first).trim() : '';
  }

  return '';
}

export function productCardPriceTitleLabel(
  config: ProductCardDisplayConfig | null | undefined,
  fallback: string
): string {
  if (!config?.priceTitle || !hasProductCardMlTextContent(config.priceTitle)) {
    return fallback;
  }
  return resolveProductCardMlLabel(config.priceTitle) || fallback;
}

export function productCardSkuTitleLabel(
  config: ProductCardDisplayConfig | null | undefined,
  fallback: string
): string {
  if (!config?.skuTitle || !hasProductCardMlTextContent(config.skuTitle)) {
    return fallback;
  }
  return resolveProductCardMlLabel(config.skuTitle) || fallback;
}

export function productCardAvailabilityLabel(
  status: 'no-available-stock' | 'limited-available-stock' | 'stock-available',
  config: ProductCardDisplayConfig | null | undefined,
  fallback: string
): string {
  if (!config) {
    return fallback;
  }

  switch (status) {
    case 'no-available-stock':
      return resolveProductCardMlLabel(config.preOrderLabel) || fallback;
    case 'limited-available-stock':
      return resolveProductCardMlLabel(config.limitedLabel) || fallback;
    case 'stock-available':
      return resolveProductCardMlLabel(config.availableLabel) || fallback;
    default:
      return fallback;
  }
}

/** Reference card height used to derive horizontal image column width from ratio. */
export const PRODUCT_CARD_HORIZONTAL_IMAGE_HEIGHT_REM = 15.75;

export function productCardHorizontalImageColumnWidth(
  config: ProductCardDisplayConfig | null | undefined
): string {
  switch (config?.imageRatio) {
    case '1:1':
      return `${PRODUCT_CARD_HORIZONTAL_IMAGE_HEIGHT_REM}rem`;
    case '3:4':
      return `${PRODUCT_CARD_HORIZONTAL_IMAGE_HEIGHT_REM * 3 / 4}rem`;
    case '16:9':
      return `${PRODUCT_CARD_HORIZONTAL_IMAGE_HEIGHT_REM * 16 / 9}rem`;
    case '4:3':
    default:
      return `${PRODUCT_CARD_HORIZONTAL_IMAGE_HEIGHT_REM * 4 / 3}rem`;
  }
}

export function productCardImageAspectRatio(
  config: ProductCardDisplayConfig | null | undefined
): string {
  switch (config?.imageRatio) {
    case '1:1':
      return '1 / 1';
    case '3:4':
      return '3 / 4';
    case '16:9':
      return '16 / 9';
    case '4:3':
    default:
      return '4 / 3';
  }
}

export function productCardImageRatioStyle(
  config: ProductCardDisplayConfig | null | undefined,
  layoutType?: ProductCardLayoutType
): Record<string, string> | null {
  const styles: Record<string, string> = {};

  if (layoutType === 'horizontal') {
    styles['--product-card-image-col-width'] = productCardHorizontalImageColumnWidth(config);
  }

  // Bootstrap ships ratio-1x1, ratio-4x3, and ratio-16x9 but not ratio-3x4.
  if (config?.imageRatio === '3:4') {
    styles['--bs-aspect-ratio'] = '133.333%';
  }

  return Object.keys(styles).length ? styles : null;
}

export function resolveProductCardFieldValue(
  product: unknown,
  path: string | undefined | null
): unknown {
  if (!product || !path?.trim()) {
    return undefined;
  }

  return path.split('.').reduce((acc: unknown, key: string) => {
    if (acc === null || acc === undefined) {
      return undefined;
    }
    if (typeof acc === 'object') {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, product);
}

export function formatProductCardFieldDisplay(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return resolveProductCardMlLabel(value as MlTextRecord);
  }
  return '';
}

const DEFAULT_TITLE_SOURCE = 'General_Information.searchKey1';
const DEFAULT_SKU_SOURCE = 'General_Information.item';
const DEFAULT_DESCRIPTION_SOURCE = 'General_Information.description';

export function productCardTitleText(
  product: unknown,
  config: ProductCardDisplayConfig | null | undefined
): string {
  const path = config?.titleSource?.trim() || DEFAULT_TITLE_SOURCE;
  return formatProductCardFieldDisplay(resolveProductCardFieldValue(product, path));
}

export function productCardSkuText(
  product: unknown,
  config: ProductCardDisplayConfig | null | undefined
): string {
  const path = config?.skuSource?.trim() || DEFAULT_SKU_SOURCE;
  return formatProductCardFieldDisplay(resolveProductCardFieldValue(product, path));
}

export function productCardDescriptionText(
  product: unknown,
  config: ProductCardDisplayConfig | null | undefined
): string {
  const path = config?.descriptionSource?.trim() || DEFAULT_DESCRIPTION_SOURCE;
  const resolved = formatProductCardFieldDisplay(resolveProductCardFieldValue(product, path));
  if (resolved || config?.descriptionSource?.trim()) {
    return resolved;
  }
  return formatProductCardFieldDisplay(
    resolveProductCardFieldValue(product, 'General_Information.productDescription')
  );
}
